import { useMemo, useState, useEffect } from 'react';

import { useNavigate } from 'react-router-dom';

import useAdvanceTable from 'hooks/useAdvanceTable';
import { useTableController } from 'hooks/useTableController';
import TableViewLayout from 'layouts/TableViewLayout';

import { fetchCustomerNodeDefinitions } from '../../../../redux/slices/customer_node_definitionsSlice';
import { getFolders } from '../../../../redux/slices/lists/foldersSlice';
import { getNodeTypes, isNodeTypeDeprecated } from '../../../../redux/slices/lists/nodeTypesSlice';
import { buildFolderPathOptions } from '../../../../helpers/folderPaths';
import { useAppSelector, useAppDispatch } from 'redux/store';
import { getActions, ActionButton, ActionDropdown, type ActionEntity } from 'actions';

interface CustomerNodeDefinition {
  node_definition_id: number;
  type: string;
  sub_type?: string;
  name: string;
  created_at: string;
  zone_name?: string;
  schedule?: any;
  status?: string;
}

const toActionEntity = (row: any): ActionEntity => ({
  type: 'node_definition',
  id: row.node_definition_id,
  zoneName: row.zone_name ?? '',
  name: row.name,
  isDeprecated: !!row.isDeprecated,
});

const ListCustomerNodeDefinitions = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const customerNodeDefinitions = useAppSelector((state) => state.customerNodeDefinitions.nodes);
  const userData = useAppSelector((state) => state.auth.userData);

  const pageName = 'node-definitions';
  const [showSidebar, setShowSidebar] = useState(false);

  const {
    nodes: nodeDefinitions,
    config: headerConfig,
    filtersConfig,
    pagination,
    error,
    loading,
    status,
  } = useAppSelector((state) => state.customerNodeDefinitions);

  const folders = useAppSelector((state) => state.listOfFolders?.folders ?? []);
  const knownNodeTypes = useAppSelector((state) => state.listOfNodeTypes?.node_types ?? []);
  const nodeTypesStatus = useAppSelector((state) => state.listOfNodeTypes?.status);

  useEffect(() => {
    dispatch(getFolders());
  }, [dispatch]);

  useEffect(() => {
    if (!nodeTypesStatus) dispatch(getNodeTypes() as any);
  }, [dispatch, nodeTypesStatus]);

  const isTableLoading = loading.fetchCustomerNodeDefinitions;

  const effectiveFiltersConfig = useMemo(() => {
    const fromBackend = filtersConfig || [];
    const keys = new Set(fromBackend.map((c: any) => c.key));
    const folderPathOptions = buildFolderPathOptions(folders, null);
    const extra: {
      key: string;
      label: string;
      type: 'search' | 'dropdown';
      default: string;
      options?: string[];
    }[] = [];
    if (!keys.has('folder')) {
      extra.push({
        key: 'folder',
        label: 'Folder (path)',
        type: 'dropdown',
        default: '',
        options: folderPathOptions.filter((o) => o.value !== '').map((o) => o.value),
      });
    }
    if (!keys.has('zone_name')) {
      extra.push({
        key: 'zone_name',
        label: 'Zone',
        type: 'search',
        default: '',
      });
    }
    const merged = [...extra, ...fromBackend];
    const typeIndex = merged.findIndex((item: any) => item.key === 'type');
    const subTypeIndex = merged.findIndex((item: any) => item.key === 'sub_type');

    if (typeIndex !== -1 && subTypeIndex !== -1 && subTypeIndex !== typeIndex + 1) {
      merged.splice(typeIndex + 1, 0, ...merged.splice(subTypeIndex, 1));
    }

    return merged;
  }, [filtersConfig, folders]);

  const {
    filters,
    setFilterAndSync,
    resetFilters,
    updateSort,
    updatePage,
    updateLimit,
    limit,
    page,
    updateUserSettings,
    getFormattedData,
    generateColumns,
    refresh,
    isSilentRefresh,
  } = useTableController({
    userData,
    pageName,
    filtersConfig: effectiveFiltersConfig,
    headerConfig,
    fetchData: fetchCustomerNodeDefinitions,
    columnFormats: { created_at: 'datetime' },
    getFormattedRow: (node: CustomerNodeDefinition) => {
      const isDeprecated = isNodeTypeDeprecated(node.type, knownNodeTypes);

      const baseType = node.type === 'shell' ? (node.sub_type ? `Shell (${node.sub_type})` : 'Shell') : node.type;
      const displayType = isDeprecated ? `${node.type} (deprecated)` : baseType;

      return {
        ...node,
        type: displayType,
        isDeprecated,
        uiStatus: node.status,
      };
    },
  });

  const actionsColumn = {
    id: 'actions',
    header: 'Actions',
    cell: ({ row }: any) => {
      const actions = getActions({ entities: [toActionEntity(row.original)], refresh, navigate });
      return (
        <ActionDropdown
          actions={actions}
          items={['duplicate', 'editFlow', 'monitorAll', 'addToTransportNodeDefinition', 'submit', 'delete']}
          disabled={row.original.isDeleting}
        />
      );
    },
  };

  // Generate columns: [checkbox, dynamic columns, actions]
  const baseColumns = generateColumns((key, order) => {
    updateSort(key, order);
    updateUserSettings(key, order);
  });

  const columns = [
    ...baseColumns.map((col: any) =>
      col.accessorKey === 'references'
        ? {
            ...col,
            meta: {
              ...col.meta,
              cellProps: { className: 'text-center' },
              headerProps: {
                ...col.meta?.headerProps,
                className: 'text-center',
              },
            },
          }
        : col
    ),
    actionsColumn,
  ];

  const formattedData = useMemo(() => {
    const data = getFormattedData(nodeDefinitions);
    return data.map((row) => ({
      ...row,
      isDeleting: row.status === 'deleting',
    }));
  }, [nodeDefinitions]);

  const table = useAdvanceTable({
    data: formattedData,
    columns,
    pageSize: limit,
    pagination: true,
    sortable: true,
    selection: true,
    getRowId: (row) => `${row.zone_name}|${row.name}`,
  });

  const selectionEntities = table.getSelectedRowModel().rows.map((row: any) => toActionEntity(row.original));
  // Toolbar mutations (bulk delete) act on the current selection; clear it after
  // so a stale selection can't fire a second action against already-removed rows.
  const toolbarActions = getActions({
    entities: selectionEntities,
    refresh: () => {
      table.resetRowSelection();
      refresh();
    },
    navigate,
  });

  return (
    <>
      <TableViewLayout
        filtersConfig={effectiveFiltersConfig}
        filters={filters}
        pageName={pageName}
        columns={headerConfig.map((c) => ({
          key: c.header_key,
          label: c.label,
        }))}
        pageSize={limit}
        error={error}
        onFilterChange={setFilterAndSync}
        onResetFilters={resetFilters}
        onPageSizeChange={updateLimit}
        currentPage={page}
        totalPages={pagination.totalPages}
        totalItems={pagination.totalItems}
        onPageChange={updatePage}
        table={table}
        showSidebar={showSidebar}
        setShowSidebar={setShowSidebar}
        onRefresh={refresh}
        toolbarButtons={
          <>
            <ActionButton actions={toolbarActions} action="delete" label="Delete Selected" />
            <ActionButton actions={toolbarActions} action="addToTransportNodeDefinition" variant="outline-primary" label="Add selected to transport" />
            <ActionButton actions={toolbarActions} action="createNodeDefinition" />
          </>
        }
        isLoading={isTableLoading}
        isSilentRefresh={isSilentRefresh}
      />
    </>
  );
};

export default ListCustomerNodeDefinitions;
